import React, { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { Plus, Edit, Search, Star, Trash2 } from 'lucide-react';
import toast from 'react-hot-toast';
import { customerReviewService } from '../../services';

const CustomerReviews = () => {
  const [items, setItems] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(false);

  const { register, handleSubmit, reset, formState: { errors } } = useForm();

  useEffect(() => { load(); }, []);

  const load = async () => {
    try {
      setLoading(true);
      const response = await customerReviewService.getAll({ search: searchTerm });
      if (response.success) {
        setItems(response.data?.data || response.data || []);
      } else {
        setItems([]);
      }
    } catch (e) {
      console.error('Error loading customer reviews:', e);
      toast.error('Failed to load customer reviews');
      setItems([]);
    } finally { setLoading(false); }
  };

  useEffect(() => {
    const t = setTimeout(() => load(), 500);
    return () => clearTimeout(t);
  }, [searchTerm]);

  const openModal = (item = null) => {
    setEditingItem(item);
    setShowModal(true);
    if (item) {
      reset({ 
        customer_name: item.customer_name, 
        review_text: item.review_text, 
        rating: item.rating,
        review_date: item.review_date || '',
        status: item.status,
        display_order: item.display_order || 0
      });
    } else { 
      reset({ 
        customer_name: '', 
        review_text: '', 
        rating: 4.5,
        review_date: '',
        status: 1,
        display_order: 0
      }); 
    }
  };

  const closeModal = () => { setShowModal(false); setEditingItem(null); reset(); };

  const onSubmit = async (data) => {
    try {
      setLoading(true);
      if (editingItem) {
        await customerReviewService.update(editingItem.id, data);
        toast.success('Customer review updated successfully!');
      } else {
        await customerReviewService.create(data);
        toast.success('Customer review created successfully!');
      }
      closeModal();
      load();
    } catch (e) {
      console.error('Error saving customer review:', e);
      toast.error('Failed to save customer review');
    } finally { setLoading(false); }
  };

  const toggleStatus = async (item) => {
    try {
      setLoading(true);
      await customerReviewService.toggleStatus(item.id);
      toast.success('Customer review status updated!');
      load();
    } catch (e) {
      console.error('Error toggling status:', e);
      toast.error('Failed to update status');
    } finally { setLoading(false); }
  };

  const handleDelete = async (item) => {
    if (!window.confirm(`Are you sure you want to delete the review from ${item.customer_name}?`)) {
      return;
    }
    try {
      setLoading(true);
      await customerReviewService.delete(item.id);
      toast.success('Customer review deleted successfully!');
      load();
    } catch (e) {
      console.error('Error deleting customer review:', e);
      toast.error('Failed to delete customer review');
    } finally { setLoading(false); }
  };

  const renderStars = (rating) => {
    const fullStars = Math.floor(rating);
    const hasHalfStar = rating % 1 !== 0;
    const emptyStars = 5 - fullStars - (hasHalfStar ? 1 : 0);
    
    return (
      <div style={{ display: 'flex', alignItems: 'center', gap: '2px', color: '#ffc107' }}>
        {[...Array(fullStars)].map((_, i) => (
          <Star key={i} size={16} fill="currentColor" />
        ))}
        {hasHalfStar && (
          <Star size={16} fill="currentColor" style={{ clipPath: 'inset(0 50% 0 0)' }} />
        )}
        {[...Array(emptyStars)].map((_, i) => (
          <Star key={i} size={16} />
        ))}
        <span style={{ marginLeft: '4px', color: '#ffc107', fontWeight: '600', fontSize: '14px' }}>
          {rating}
        </span>
      </div>
    );
  };

  return (
    <div>
      <div className="card">
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px' }}>
          <h1 style={{ fontSize: '24px', fontWeight: '600', color: '#111827' }}>Customer Reviews</h1>
          <button onClick={() => openModal()} className="btn btn-primary">
            <Plus size={18} />
            Add Review
          </button>
        </div>

        <div style={{ marginBottom: '20px', position: 'relative', maxWidth: '300px' }}>
          <Search size={18} style={{ position: 'absolute', left: '12px', top: '50%', transform: 'translateY(-50%)', color: '#6b7280' }} />
          <input type="text" placeholder="Search reviews..." value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} className="search-box" style={{ paddingLeft: '40px' }} />
        </div>

        <div style={{ overflowX: 'auto' }}>
          <table className="table">
            <thead>
              <tr>
                <th>ID</th>
                <th>Customer Name</th>
                <th>Review</th>
                <th>Rating</th>
                <th>Date</th>
                <th>Order</th>
                <th>Status</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {items.map((item) => (
                <tr key={item.id}>
                  <td>{item.id}</td>
                  <td style={{ fontWeight: '500' }}>{item.customer_name}</td>
                  <td>
                    <div style={{ maxWidth: '400px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', color: '#6b7280' }}>
                      {item.review_text}
                    </div>
                  </td>
                  <td>{renderStars(parseFloat(item.rating))}</td>
                  <td>{item.review_date || '-'}</td>
                  <td>{item.display_order || 0}</td>
                  <td>
                    <button onClick={() => toggleStatus(item)} className={`status-badge ${item.status === 1 ? 'status-active' : 'status-inactive'}`} style={{ cursor: 'pointer', border: 'none' }} disabled={loading}>
                      {item.status === 1 ? 'Active' : 'Inactive'}
                    </button>
                  </td>
                  <td>
                    <div style={{ display: 'flex', gap: '8px' }}>
                      <button onClick={() => openModal(item)} className="btn btn-secondary" style={{ padding: '6px 8px', fontSize: '12px' }}>
                        <Edit size={14} />
                      </button>
                      <button onClick={() => handleDelete(item)} className="btn btn-secondary" style={{ padding: '6px 8px', fontSize: '12px', backgroundColor: '#ef4444', color: 'white' }}>
                        <Trash2 size={14} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {loading && (<div style={{ textAlign: 'center', padding: '40px', color: '#6b7280' }}>Loading reviews...</div>)}
        {!loading && items.length === 0 && (<div style={{ textAlign: 'center', padding: '40px', color: '#6b7280' }}>No reviews found.</div>)}
      </div>

      {showModal && (
        <div className="modal-overlay" onClick={closeModal}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2 className="modal-title">{editingItem ? 'Edit Customer Review' : 'Add New Customer Review'}</h2>
              <button onClick={closeModal} className="close-btn">×</button>
            </div>

            <form onSubmit={handleSubmit(onSubmit)}>
              <div className="form-group">
                <label className="form-label">Customer Name</label>
                <input type="text" className="form-input" placeholder="Enter customer name" {...register('customer_name', { required: 'Customer name is required', minLength: { value: 2, message: 'Customer name must be at least 2 characters' } })} />
                {errors.customer_name && (<div className="error-message">{errors.customer_name.message}</div>)}
              </div>

              <div className="form-group">
                <label className="form-label">Review Text</label>
                <textarea className="form-input" placeholder="Enter review text" rows={4} {...register('review_text', { required: 'Review text is required', minLength: { value: 10, message: 'Review text must be at least 10 characters' } })} />
                {errors.review_text && (<div className="error-message">{errors.review_text.message}</div>)}
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
                <div className="form-group">
                  <label className="form-label">Rating (0-5)</label>
                  <input type="number" step="0.1" min="0" max="5" className="form-input" placeholder="4.5" {...register('rating', { required: 'Rating is required', min: { value: 0, message: 'Rating must be at least 0' }, max: { value: 5, message: 'Rating must be at most 5' } })} />
                  {errors.rating && (<div className="error-message">{errors.rating.message}</div>)}
                </div>

                <div className="form-group">
                  <label className="form-label">Review Date (e.g., "Oct 2024")</label>
                  <input type="text" className="form-input" placeholder="Oct 2024" {...register('review_date')} />
                </div>
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
                <div className="form-group">
                  <label className="form-label">Display Order</label>
                  <input type="number" className="form-input" placeholder="0" {...register('display_order', { valueAsNumber: true })} />
                  <small style={{ color: '#6b7280', fontSize: '12px' }}>Lower numbers appear first</small>
                </div>

                <div className="form-group">
                  <label className="form-label">Status</label>
                  <select className="form-select" {...register('status', { required: 'Status is required', valueAsNumber: true })}>
                    <option value={1}>Active</option>
                    <option value={0}>Inactive</option>
                  </select>
                  {errors.status && (<div className="error-message">{errors.status.message}</div>)}
                </div>
              </div>

              <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end', marginTop: '24px' }}>
                <button type="button" onClick={closeModal} className="btn btn-secondary">Cancel</button>
                <button type="submit" className="btn btn-primary" disabled={loading}>{loading ? 'Saving...' : editingItem ? 'Update Review' : 'Create Review'}</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default CustomerReviews;

